/* eslint-disable react-hooks/exhaustive-deps */
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import { getBiddingSummary, getBiddingTimeline } from 'store/modules/management_store/actions';
import {
  BiddingData,
  BiddingSummaryFinished,
  ManagementState,
} from 'store/modules/management_store/types';
import TextFilter from 'components/atoms/text_filter';
import _ from 'underscore';
import companyId from 'services/company_id';
import { ProfileState } from 'store/modules/profile_store/types';
import createTrack from 'services/actions/tracker_actions';
import useOnMutationSettled from 'hooks/useOnMutationSettled';
import ManagementTask from '../management_task';
import { Container } from './styles';

interface BiddingTimelineProps {
  bidding: BiddingData;
}

interface timelineEventsTypes {
  date: string;
  className: string;
  label: string;
  title: string;
  description: string;
  total_attachments: number;
  total_comments: number;
  id: number;
}

const BiddingTimeline = (props: BiddingTimelineProps) => {
  const { bidding } = props;

  const { management, request } = useSelector<ApplicationState, ManagementState>(
    (state) => state.session.management,
  );

  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);

  const timeLineEvents = () => {
    const events: Array<timelineEventsTypes> = [
      ...(management.timeline_tasks || [])
        .filter((task) => task.status_task_id === 'finished')
        .map((task) => {
          return {
            date: task.finished_date || '',
            className: 'success',
            label: 'TAREFA',
            title: task.title,
            description: task.description,
            total_attachments: task.bidding_attachments?.length,
            total_comments: task.task_event_comments?.length,
            id: task.id,
          };
        }),
      ...(management.timeline_progress || []).map((notification_progress) => {
        return {
          date: notification_progress?.progress?.deadline || '',
          className: 'primary',
          label: 'ANDAMENTO',
          title: notification_progress?.progress?.title,
          description: notification_progress?.progress?.description,
          total_attachments: notification_progress?.progress?.bidding_attachments?.length,
          total_comments: notification_progress?.progress?.task_event_comments?.length,
          id: notification_progress?.progress?.id,
        };
      }),
    ];
    return _.sortBy(events, (event: timelineEventsTypes) => {
      return Date.parse(event.date as string);
    }).reverse();
  };

  const [finished, setFinished] = useState<BiddingSummaryFinished | null>(null);
  const [modalManagementTaskOpened, setModalManagementTaskOpened] = useState<boolean>(false);
  const [currentEvent, setCurrentEvent] = useState<timelineEventsTypes | null>(null);
  const [filteredList, setFilteredList] = useState<Array<timelineEventsTypes>>([]);
  const [filterValue, setFilterValue] = useState<string>('');
  const [showTasks, setShowTasks] = useState<boolean>(true);
  const [showProgresses, setShowProgresses] = useState<boolean>(true);
  const [orderAsc, setOrderAsc] = useState<boolean>(false);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getBiddingTimeline(companyId(profile), bidding.id));
    dispatch(getBiddingSummary(bidding.id, companyId(profile)));
  }, [bidding?.id]);

  const biddingSummary = management.bidding_summary?.[bidding.id];

  useEffect(() => {
    setFinished(biddingSummary?.finished ?? null);
  }, [biddingSummary?.finished]);

  const modalWasOpenRef = useRef<boolean>(false);
  const refreshTimeline = () => {
    dispatch(getBiddingTimeline(companyId(profile), bidding.id));
    dispatch(getBiddingSummary(bidding.id, companyId(profile)));
  };
  useEffect(() => {
    if (modalWasOpenRef.current && !modalManagementTaskOpened) refreshTimeline();
    modalWasOpenRef.current = modalManagementTaskOpened;
  }, [modalManagementTaskOpened]);

  useOnMutationSettled(
    [
      request.create_tasks?.loaded,
      request.update_tasks?.loaded,
      request.delete_task?.loaded,
      request.update_tasks_status_batch?.loaded,
      request.create_progress?.loaded,
      request.update_progress?.loaded,
      request.delete_progress?.loaded,
    ],
    () => {
      if (modalManagementTaskOpened) return;
      refreshTimeline();
    },
  );

  useEffect(() => {
    let rawList = timeLineEvents();

    if (orderAsc)
      rawList = _.sortBy(rawList, (event: timelineEventsTypes) => {
        return Date.parse(event.date as string);
      });
    else
      rawList = _.sortBy(rawList, (event: timelineEventsTypes) => {
        return Date.parse(event.date as string);
      }).reverse();

    if (showTasks && showProgresses && filterValue === '') return setFilteredList(rawList);

    if (!showTasks) rawList = rawList.filter((item) => item.label !== 'TAREFA');
    if (!showProgresses) rawList = rawList.filter((item) => item.label !== 'ANDAMENTO');

    if (filterValue !== '')
      rawList = rawList.filter(
        (item) =>
          item.title.toLocaleLowerCase().includes(filterValue.toLocaleLowerCase()) ||
          item.description.toLocaleLowerCase().includes(filterValue.toLocaleLowerCase()),
      );

    return setFilteredList(rawList);
  }, [
    showTasks,
    showProgresses,
    filterValue,
    orderAsc,
    request.get_bidding_timeline?.loaded,
    management.timeline_progress,
    management.timeline_tasks,
  ]);

  return (
    <Container className="row mt-5">
      <div className="col-12">
        <div className="shadow bg-white">
          <div className="border-bottom p-3 row">
            <div className="col-md-2 col-12 font-weight-bold d-flex align-items-center">
              LINHA DO TEMPO
              <button
                className="btn btn-link p-0"
                type="button"
                onClick={() => setOrderAsc(!orderAsc)}
              >
                {orderAsc ? (
                  <span className="material-icons">arrow_drop_up</span>
                ) : (
                  <span className="material-icons">arrow_drop_down</span>
                )}
              </button>
            </div>
            <div className="col-md-10 col-12 mt-2 mt-md-0 d-block d-md-flex">
              <TextFilter textFilterValue={filterValue} setTextFilterValue={setFilterValue} />
              <button
                type="button"
                className={`btn mr-1 btn-sm ${
                  !showProgresses ? 'btn-outline-primary' : 'btn-primary'
                }`}
                onClick={() => setShowProgresses(!showProgresses)}
              >
                Andamentos
              </button>
              <button
                type="button"
                className={`btn btn-sm ${!showTasks ? 'btn-outline-primary' : 'btn-primary'}`}
                onClick={() => setShowTasks(!showTasks)}
              >
                Tarefas
              </button>
              {biddingSummary?.is_managed && (
                <button
                  type="button"
                  className="btn ml-md-auto ml-1 btn-primary btn-sm"
                  onClick={() => {
                    setCurrentEvent(null);
                    setModalManagementTaskOpened(true);
                    createTrack('Card - Adicinou andamento', { fonte: 'biddingDetails' });
                  }}
                >
                  Adicionar andamento
                </button>
              )}
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <ul className="list-unstyled m-0">
                {finished && (
                  <li>
                    <div className="row m-0 p-0">
                      <div className="col-2 p-3 bg-light border-right text-right">
                        <small className="font-weight-bold d-md-block d-none">
                          {moment(finished.created_at).format('L')}
                        </small>
                      </div>
                      <div className="p-3 bullet col-9">
                        <small className="font-weight-bold d-md-none d-block">
                          {moment(finished.created_at).format('L')}
                        </small>
                        <span className="badge badge-pill px-2 py-1 mb-1 badge-danger">
                          FINALIZAÇÃO
                        </span>
                        <p className="m-0">
                          {finished.management_finished_status === 'lost' && (
                            <span className="m-0 font-weight-bold">A licitação foi perdida</span>
                          )}
                          {finished.management_finished_status === 'cancelled' && (
                            <span className="m-0 font-weight-bold">A licitação foi cancelada</span>
                          )}
                          {finished.management_finished_status === 'failed' && (
                            <span className="m-0 font-weight-bold">A licitação foi fracassada</span>
                          )}
                          {finished.management_finished_status === 'revoked' && (
                            <span className="m-0 font-weight-bold">A licitação foi revogada</span>
                          )}
                          {finished.management_finished_status === 'declined' && (
                            <span className="m-0 font-weight-bold">Declinamos da participação</span>
                          )}
                          {finished.management_finished_status === 'winner' && (
                            <span className="m-0 font-weight-bold">Vencedor em todos os itens</span>
                          )}
                          {finished.management_finished_status === 'partial_winner' && (
                            <span className="m-0 font-weight-bold">Vencedor de alguns itens</span>
                          )}
                        </p>
                        {(finished.management_finished_status === 'winner' ||
                          finished.management_finished_status === 'partial_winner') &&
                          finished.amount && (
                            <p className="text-success m-0 font-italic">
                              {`Valor total ganho na licitação: R$ ${finished.amount}`}
                            </p>
                          )}
                        {finished.comments && <p className="m-0">{finished.comments}</p>}
                      </div>
                    </div>
                  </li>
                )}
                {filteredList.map((event) => {
                  return (
                    <li key={`progress_${event.id}`}>
                      <div className="row m-0 p-0">
                        <div className="col-2 p-3 bg-light border-right text-right">
                          <small className="font-weight-bold d-md-block d-none">
                            {moment(event.date).format('L')}
                          </small>
                        </div>
                        <div className="p-3 bullet col-9">
                          <small className="font-weight-bold d-md-none d-block">
                            {moment(event.date).format('L')}
                          </small>
                          <span
                            className={`badge badge-pill px-2 py-1 mb-1 badge-${event.className}`}
                          >
                            {event.label}
                          </span>
                          <p className="m-0">
                            <span className="m-0 font-weight-bold">{event.title}</span>
                          </p>
                          <p className="m-0">{event.description}</p>
                          <div className="p-0 text-secondary d-flex flex-md-row flex-column">
                            <button
                              type="button"
                              className="mr-4 btn btn-link p-0 text-secondary text-left"
                              onClick={() => {
                                setCurrentEvent(event);
                                setModalManagementTaskOpened(true);
                              }}
                            >
                              <small>Visualizar</small>
                            </button>
                            <div className="d-flex align-items-center mr-4">
                              <small>{`${event.total_attachments} anexo(s)`}</small>
                              <span className="material-icons small-icon">attach_file</span>
                            </div>
                            <div className="d-flex align-items-center">
                              <small>{`${event.total_comments} comentários`}</small>
                              <span className="material-icons small-icon ml-1">forum</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
          <ManagementTask
            bidding={bidding}
            taskType={currentEvent?.label === 'TAREFA' ? 'task' : 'progress'}
            modalManagementTaskOpened={modalManagementTaskOpened}
            setModalManagementTaskOpened={setModalManagementTaskOpened}
            task={
              currentEvent?.label === 'TAREFA'
                ? (management.timeline_tasks || []).find((task) => task?.id === currentEvent?.id)
                : (management.timeline_progress || []).find(
                    (notification_progress) =>
                      notification_progress?.progress?.id === currentEvent?.id,
                  )?.progress
            }
          />
        </div>
      </div>
    </Container>
  );
};

export default BiddingTimeline;
